import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HeaderBread from "../../../../components/HeaderBread";
import Layout from "../../../../Layout";
import SwitchBar from "../../../../components/Pages/SwitchBar";
import SearchField from "../../../../components/SearchField";
import SortableTable from "../../../../components/UI/SortableTable";
import { useDataLayerValue } from "../../../../DataLayer/DataLayer";
import { useDebounce } from "../../../../hooks";
import { checkPermission } from "../../../../utils/Validation/checkPermission";
import { getVessels } from "../../../../utils/ApiCalls/SpecialPage/index";
import { getEntertainmentRvcs } from "../../../../utils/ApiCalls/BackOffice/Entertainment";

const PAGE_PATH = "/shore/back-office/entertainment/revenue-center";
const MANAGEMENT_PATH = "/shore/back-office/entertainment/management";

const EntertainmentRevenueCenter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [{ permission }] = useDataLayerValue();

  const [vesselOptions, setVesselOptions] = useState([]);
  const [vesselId, setVesselId] = useState(() => {
    const urlVesselId = new URLSearchParams(location.search).get("vesselId");
    return urlVesselId ? Number(urlVesselId) : "";
  });

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 1000);

  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(location.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(location.search).get("pageSize")) || 10
  );
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const loadVessels = async () => {
      const vessels = await getVessels();
      const allowed = (vessels || []).filter((item) =>
        checkPermission(
          permission,
          "read_per",
          "Revenue Center",
          `${PAGE_PATH}/${item.name.toLowerCase().replaceAll(" ", "-")}`
        )
      );
      setVesselOptions(allowed);

      const currentVesselId = new URLSearchParams(location.search).get("vesselId");
      const isCurrentAllowed = allowed.some((v) => String(v.id) === String(currentVesselId));
      if (!isCurrentAllowed && allowed.length) {
        setVesselId(allowed[0].id);
      }
    };
    loadVessels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (vesselId) params.set("vesselId", vesselId);
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (params.toString() !== location.search.replace(/^\?/, "")) {
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselId, page, pageSize, search]);

  const loadRvcs = async (targetPage) => {
    if (!vesselId) return;
    const res = await getEntertainmentRvcs({
      vesselId,
      page: targetPage,
      limit: pageSize,
      search: debouncedSearch || undefined,
    });
    if (res?.error) return;
    setRows(res.data || []);
    setTotalCount(res.totalCount || 0);
  };

  useEffect(() => {
    if (!vesselId) return;
    loadRvcs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselId, page, pageSize, debouncedSearch]);

  const columns = [
    {
      title: "Sl",
      key: "sl",
      align: "center",
      width: 60,
      render: (value, row, index) => (page - 1) * pageSize + index + 1,
    },
    { title: "RVC ID", key: "rvcId", align: "center" },
    {
      title: "Name",
      key: "name",
      render: (value, row) => (
        <Link to={`${PAGE_PATH}/${row.id}`}>{value}</Link>
      ),
    },
    { title: "Name (SC)", key: "nameSimplifiedChinese" },
    { title: "Name (TC)", key: "nameTraditionalChinese" },
  ];

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Revenue Center", route: PAGE_PATH },
  ];

  return (
    <Layout type={"read_per"} module_data={"Revenue Center"}>
      <div>
        <HeaderBread navigation={navigation} title={"Revenue Center"} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <div className="card">
            <div className="card-header two_item">
              <h5 className="card-title">Revenue Centers</h5>
              <div className="two_item">
                <Link
                  to={vesselId ? `${MANAGEMENT_PATH}/add?vesselId=${vesselId}` : `${MANAGEMENT_PATH}/add`}
                  className="btn add_v_btn mx-2"
                >
                  Add Revenue Center
                </Link>
              </div>
            </div>
            <div className="card-body">
              <div className="table_holder">
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                  }}
                >
                  <SwitchBar
                    tabs={vesselOptions.map((item) => ({
                      title: item.name,
                      value: item.id,
                    }))}
                    activeTab={vesselId}
                    setActiveTab={(val) => {
                      setVesselId(val);
                      setPage(1);
                    }}
                  />

                  <div style={{ maxWidth: 200 }}>
                    <SearchField
                      value={search}
                      setValue={(e) => setSearch(e.target.value)}
                      SearchValue={() => setPage(1)}
                      placeholder="Search"
                    />
                  </div>
                </div>

                <SortableTable
                  columns={columns}
                  rows={rows}
                  page={page}
                  setPage={setPage}
                  count={totalCount}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EntertainmentRevenueCenter;
